import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { Task, SequenceFlow } from "model/activitiesModel";
import { Priority } from "hooks/usePriorities";
import { UserData } from "hooks/useUserDataKeys";
import { CompleteComment } from "components/CommentsSection";
import { HistoryEntry } from "components/HistoriesSection";
import MobileDetailHeader from "./MobileDetailHeader";
import MobileDetailToolbar from "./MobileDetailToolbar";
import MobileDetailTabBar, { MobileDetailTabValue } from "./MobileDetailTabBar";
import MobileDetailContent from "./MobileDetailContent";
import MobileDetailComments from "./MobileDetailComments";
import MobileDetailFooterDock from "./MobileDetailFooterDock";
import FieldsList from "components/FieldsList";
import type { MDXEditorMethods } from "@mdxeditor/editor";

export interface MobileTaskDetailDrawerProps {
  task: Task;
  onClose: () => void;
  onTakeInCarico: (task: Task) => void | Promise<void>;
  onUndo: (task: Task) => void | Promise<void>;
  onSequenceFlowChoice: (task: Task, flow: SequenceFlow) => void | Promise<void>;
  onPriorityChange: (task: Task, priority: Priority) => void | Promise<void>;
  administrator: boolean;
  historyMode: boolean;
  doing: boolean;
  readonly: boolean;
  translations: Record<string, string>;

  // Document preview / share callbacks (TaskDetailDialog reuses these).
  onDownload?: () => void;
  onPreview?: () => void;
  onShare?: () => void;
  downloadingDocument?: boolean;

  // Comments / history / attachments state lifted from TaskDetailDialog.
  comments: CompleteComment[];
  history: HistoryEntry[];
  onComment: (text: string) => Promise<void>;
  onEditComment: (id: number, text: string) => Promise<void>;

  attachments: any[];
  images: { [id: number]: string };
  attachmentHistories: { [id: number]: any[] };
  getAttachments: () => Promise<void>;
  handleUploadFile: (files: File[]) => Promise<void>;

  // Fields / userdata state.
  userdataEntries: { [key: string]: UserData };
  setUserDataEntries: React.Dispatch<React.SetStateAction<{ [key: string]: UserData }>>;
  openAddFieldDialog: boolean;
  setOpenAddFieldDialog: (v: boolean) => void;
  /** Save userdata callback used by the FieldsList (ghost + inline) on submit. */
  handleSaveUserdata?: (payload: { [key: string]: UserData }) => Promise<void>;
  refresh?: () => void;
  sendSnack: (payload: { type?: "success" | "error" | "info" | "warning"; message: string }) => void;

  // Rich-text description.
  descriptionDetail: string | null;
  descriptionDetailReadOnly: boolean;
  descriptionDetailRef: React.RefObject<MDXEditorMethods>;
  isDescriptionDetailFocused: boolean;
  setIsDescriptionDetailFocused: (v: boolean) => void;
  saveDescriptionDetail: () => void;
  setShowEditClosedConfirm: (v: boolean) => void;

  // Priorities.
  priorities: Priority[];
  selectedPriority: Priority | null;

  // Toolbar attachments: anchorEl + label manager.
  driveAnchorEl: HTMLElement | null;
  onDriveOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onDriveClose: () => void;
  isLabelManagerOpen: boolean;
  onLabelManagerOpen: () => void;
  onLabelManagerClose: () => void;
}

/**
 * Mobile (portrait) bottom-sheet wrapper for the task detail.
 * Composes header + toolbar + tabbar + (content | comments | history) + footer dock.
 */
const MobileTaskDetailDrawer: React.FC<MobileTaskDetailDrawerProps> = ({
  task,
  onClose,
  onTakeInCarico,
  onUndo,
  onSequenceFlowChoice,
  onPriorityChange,
  administrator,
  historyMode,
  doing,
  readonly,
  translations,
  onDownload,
  onPreview,
  onShare,
  downloadingDocument,
  comments,
  history,
  onComment,
  onEditComment,
  attachments,
  images,
  attachmentHistories,
  getAttachments,
  handleUploadFile,
  userdataEntries,
  setUserDataEntries,
  openAddFieldDialog,
  setOpenAddFieldDialog,
  handleSaveUserdata,
  refresh,
  sendSnack,
  descriptionDetail,
  descriptionDetailReadOnly,
  descriptionDetailRef,
  isDescriptionDetailFocused,
  setIsDescriptionDetailFocused,
  saveDescriptionDetail,
  setShowEditClosedConfirm,
  priorities,
  selectedPriority,
  driveAnchorEl,
  onDriveOpen,
  onDriveClose,
  isLabelManagerOpen,
  onLabelManagerOpen,
  onLabelManagerClose,
}) => {
  const [tab, setTab] = useState<MobileDetailTabValue>("data");

  // `canComment` gates the comments editor — we only let users write
  // comments while the task is in the "doing" state, regardless of who
  // opened the dialog.
  const canComment = doing && !readonly;

  // `canTransition` gates the workflow actions at the bottom of the
  // drawer (Prendi in Carico, Riporta, sequence flow). It must NOT be
  // tied to `doing`, otherwise the "Prendi in Carico" CTA on a `todo`
  // task would be permanently disabled. We only block transitions on
  // readonly/historyMode or while a request is already in flight.
  const canTransition = !readonly;

  // The footer dock is only meaningful for todo/doing states; for history we hide it.
  const showFooterDock = !historyMode && (task.state === 0 || task.state === 1);

  const isSequenceFlow = useMemo(
    () => (task.sequenceflows?.length ?? 0) > 0,
    [task.sequenceflows],
  );

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        overflow: "hidden",
        // Safe areas are applied at the Dialog Paper level
        // (see TaskDetailDialog.tsx) so we don't double-pad here.
      }}
    >
      <MobileDetailHeader
        task={task}
        onClose={onClose}
        onDownload={onDownload}
        onPreview={onPreview}
        onShare={onShare}
        disabled={downloadingDocument}
      />

      <MobileDetailToolbar
        priorities={priorities}
        selectedPriority={selectedPriority}
        onPriorityChange={(p) => onPriorityChange(task, p)}
        onOpenLabels={onLabelManagerOpen}
        onOpenDrive={onDriveOpen}
        onAddField={() => setOpenAddFieldDialog(true)}
        isEditable={canTransition}
        translations={translations}
        driveAnchorEl={driveAnchorEl}
        onDriveClose={onDriveClose}
        isLabelManagerOpen={isLabelManagerOpen}
        onLabelManagerClose={onLabelManagerClose}
        idprocess={task.idprocess}
      />

      <MobileDetailTabBar
        value={tab}
        onChange={setTab}
        commentsCount={comments.length}
        historyCount={history.length}
        translations={translations}
      />

      <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {tab === "data" ? (
          <MobileDetailContent
            task={task}
            descriptionDetail={descriptionDetail}
            descriptionDetailReadOnly={descriptionDetailReadOnly}
            descriptionDetailRef={descriptionDetailRef}
            isDescriptionDetailFocused={isDescriptionDetailFocused}
            setIsDescriptionDetailFocused={setIsDescriptionDetailFocused}
            saveDescriptionDetail={saveDescriptionDetail}
            setShowEditClosedConfirm={setShowEditClosedConfirm}
            userdataEntries={userdataEntries}
            setUserDataEntries={setUserDataEntries}
            // The FieldsList inside MobileDetailContent does NOT own
            // the AddField dialog anymore — we render a "ghost"
            // FieldsList below that is always mounted so the dialog
            // works from any tab. We still pass setOpenAddFieldDialog
            // so the inline list UI can refresh itself after the
            // dialog closes, but we do NOT pass openAddFieldDialog
            // here to avoid the inline FieldsList racing the ghost.
            setOpenAddFieldDialog={setOpenAddFieldDialog}
            attachments={attachments}
            images={images}
            histories={attachmentHistories}
            getAttachments={getAttachments}
            handleUploadFile={handleUploadFile}
            doing={doing}
            readonly={readonly}
            refresh={refresh}
            sendSnack={sendSnack}
            translations={translations}
          />
        ) : (
          <MobileDetailComments
            value={tab === "history" ? "history" : "comments"}
            comments={comments}
            history={history}
            canWrite={canComment}
            canEdit={canComment}
            onComment={onComment}
            onEditComment={onEditComment}
          />
        )}
      </Box>

      {showFooterDock && (
        <MobileDetailFooterDock
          task={task}
          onTakeInCarico={() => onTakeInCarico(task)}
          onUndo={() => onUndo(task)}
          onSequenceFlowChoice={(flow) => onSequenceFlowChoice(task, flow)}
          isEditable={canTransition}
          translations={translations}
        />
      )}

      {/*
        Ghost FieldsList: always mounted (but visually hidden) so the
        "AddField" dialog it owns can be opened from the toolbar in
        *any* tab — including Commenti and Storia — without unmounting
        the FieldsList component. We keep the actual list UI disabled
        with readonly here because the user only needs the dialog
        reachable; the editable list lives inside MobileDetailContent.
      */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          clipPath: "inset(50%)",
          whiteSpace: "nowrap",
          pointerEvents: "none",
        }}
      >
        <FieldsList
          canManage={false}
          schema={task.userschema}
          selectedProcess={task.idprocess}
          value={userdataEntries}
          onChange={setUserDataEntries}
          openAddFieldDialog={openAddFieldDialog}
          onAddFieldDialogClose={() => setOpenAddFieldDialog(false)}
          handleSaveUserdata={handleSaveUserdata}
          readonly
        />
      </Box>

      {/* Unused but kept for parity with desktop admin gates */}
      <Box sx={{ display: "none" }}>{`${administrator}|${historyMode}`}</Box>
    </Box>
  );
};

export default MobileTaskDetailDrawer;
